import { api } from "../../api/apiSlice";

/**
 * `/notifications/*` - the caller's OWN notifications, nothing else. The
 * controller carries a bare [Authorize] with no permission of its own: there is
 * nothing to gate, since a user can only ever read the rows addressed to them.
 *
 * Same four calls the Flutter client makes (see
 * TexasAlarms_APP_FLUTTER/lib/features/notifications/data/notifications_api.dart) -
 * the two clients are looking at the same inbox, so they must agree on what
 * reading and clearing mean.
 *
 * Device-token registration is deliberately NOT here: web push would need a
 * service worker and a Firebase web app registration that this app does not have.
 * The bell polls unread-count instead, which is what the Flutter bell does too.
 */
export const notificationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listNotifications: builder.query({
      query: (params = { page: 1, pageSize: 30 }) => ({ url: "/notifications", params }),
      providesTags: [{ type: "Notification", id: "LIST" }],
    }),
    // Its own endpoint rather than counting the list client-side: the bell is on
    // every screen and polls, so it must stay a single cheap integer read.
    getUnreadNotificationCount: builder.query({
      query: () => ({ url: "/notifications/unread-count" }),
      providesTags: [{ type: "Notification", id: "COUNT" }],
    }),
    markNotificationRead: builder.mutation({
      query: (recordGuid) => ({ url: `/notifications/${recordGuid}/read`, method: "PUT" }),
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "Notification", id: "COUNT" },
      ],
    }),
    // Wipes every row, read or unread - not "mark all read". Same destructive
    // meaning as the mobile screen's own Clear all, so it is confirmed first.
    clearNotifications: builder.mutation({
      query: () => ({ url: "/notifications", method: "DELETE" }),
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "Notification", id: "COUNT" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationReadMutation,
  useClearNotificationsMutation,
} = notificationsApi;
